/**
 * Document-type icons for participant training uploads
 * (**`.txt`** **`.pdf`** **`.docx`** **`.xlsx`** mirror **`training_material_formats.json`**).
 *
 * **`.docx`**, **`.xlsx`**, and **`.pdf`** use the same single-colour vector shapes as the
 * [Simple Icons](https://github.com/simple-icons/simple-icons) release **11.14.0**
 * (**CC0-1.0**), with fills from that dataset (**Word** **#2B579A**, **Excel** **#217346**,
 * **Adobe Acrobat Reader** **#EC1C24**). MICROSOFT, EXCEL, Word, Acrobat, Adobe, PDF, etc.
 * may be trademarks of Microsoft Corporation or Adobe Inc. Redistributable product artwork
 * is controlled by vendor programs; consumers should follow Microsoft's and Adobe's
 * trademark guides for brand-critical surfaces.
 */

export type TrainingMaterialIconKind =
  | "pdf"
  | "word"
  | "excel"
  | "text"
  | "generic";

/** Returns the final path segment trimmed. */
export function trainingMaterialBasename(filename: string): string {
  const trimmed = filename.trim();
  if (!trimmed) {
    return "";
  }
  const parts = trimmed.split(/[/\\]/);
  return (parts[parts.length - 1] ?? "").trim();
}

/**
 * Normalizes **`original_filename`** suffix to lowercase leading-dot extension, or **`""`**.
 *
 * @param filename - Organizer-supplied **`original_filename`** (may include folders or odd spacing).
 */
export function normalizedTrainingMaterialExtension(filename: string): string {
  const base = trainingMaterialBasename(filename);
  const dot = base.lastIndexOf(".");
  if (dot <= 0 || dot === base.length - 1) {
    return "";
  }
  return base.slice(dot).toLowerCase();
}

/**
 * Maps allowed training extensions (**backend** **`training_material_formats.json`**) to icon keys.
 *
 * @param extension - **`normalizedTrainingMaterialExtension`** output (with leading dot).
 */
export function trainingMaterialIconKind(
  extension: string,
): TrainingMaterialIconKind {
  switch (extension) {
    case ".pdf":
      return "pdf";
    case ".docx":
      return "word";
    case ".xlsx":
      return "excel";
    case ".txt":
      return "text";
    default:
      return "generic";
  }
}

const SIMPLE_ICONS_MICROSOFT_WORD_D =
  "M23.004 1.5q.41 0 .703.293t.293.703v19.008q0 .41-.293.703t-.703.293H6.996q-.41 0-.703-.293T6 21.504V18H.996q-.41 0-.703-.293T0 17.004V6.996q0-.41.293-.703T.996 6H6V2.496q0-.41.293-.703t.703-.293zM6.035 11.203l1.442 4.735h1.64l1.57-7.876H9.036l-.937 4.653-1.325-4.5H5.38l-1.406 4.523-.938-4.675H1.312l1.57 7.874h1.641zM22.5 21v-3h-15v3zm0-4.5v-3.75H12v3.75zm0-5.25V7.5H12v3.75zm0-5.25V3h-15v3Z";

const SIMPLE_ICONS_MICROSOFT_EXCEL_D =
  "M23 1.5q.41 0 .7.3.3.29.3.7v19q0 .41-.3.7-.29.3-.7.3H7q-.41 0-.7-.3-.3-.29-.3-.7V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h5V2.5q0-.41.3-.7.29-.3.7-.3zM6 13.28l1.42 2.66h2.14l-2.38-3.87 2.34-3.8H7.46l-1.3 2.4-.05.08-.04.09-.64-1.28-.66-1.29H2.59l2.27 3.82-2.48 3.85h2.16zM14.25 21v-3H7.5v3zm0-4.5v-3.75H12v3.75zm0-5.25V7.5H12v3.75zm0-5.25V3H7.5v3zm8.25 15v-3h-6.75v3zm0-4.5v-3.75h-6.75v3.75zm0-5.25V7.5h-6.75v3.75zm0-5.25V3h-6.75v3Z";

const SIMPLE_ICONS_ADOBE_ACROBAT_READER_D =
  "M23.63 15.3c-.71-.745-2.166-1.17-4.224-1.17-1.1 0-2.377.106-3.761.354a19.443 19.443 0 0 1-2.307-2.661c-.532-.71-.994-1.49-1.42-2.236.817-2.484 1.207-4.507 1.207-5.962 0-1.632-.603-3.336-2.342-3.336-.532 0-1.065.32-1.349.781-.78 1.384-.425 4.4.923 7.381a60.277 60.277 0 0 1-1.703 4.507c-.568 1.349-1.207 2.733-1.917 4.01C2.834 18.53.314 20.34.03 21.758c-.106.533.071 1.03.462 1.42.142.107.639.533 1.49.533 2.59 0 5.323-4.188 6.707-6.707 1.065-.355 2.13-.71 3.194-.994a34.963 34.963 0 0 1 3.407-.745c2.732 2.448 5.145 2.839 6.352 2.839 1.49 0 2.023-.604 2.2-1.1.32-.64.106-1.349-.213-1.704zm-1.42 1.03c-.107.532-.64.887-1.384.887-.213 0-.39-.036-.604-.071-1.348-.32-2.626-.994-3.903-2.059a17.717 17.717 0 0 1 2.98-.248c.746 0 1.385.035 1.81.142.497.106 1.278.426 1.1 1.348zm-7.524-1.668a38.01 38.01 0 0 0-2.945.674 39.68 39.68 0 0 0-2.52.745 40.05 40.05 0 0 0 1.207-2.555c.426-.994.78-2.023 1.136-2.981.354.603.745 1.207 1.135 1.739a50.127 50.127 0 0 0 1.987 2.378zM10.038 1.46a.768.768 0 0 1 .674-.425c.745 0 .887.851.887 1.526 0 1.135-.355 2.874-.958 4.861-1.03-2.768-1.1-5.074-.603-5.962zM6.134 17.997c-1.81 2.981-3.549 4.826-4.613 4.826a.872.872 0 0 1-.532-.177c-.213-.213-.32-.461-.249-.745.213-1.065 2.271-2.555 5.394-3.904Z";

/** Single-path brand marks built from **`SIMPLE_ICONS_*_D`** (see module doc). */
function appendSimpleIconGlyph(
  svg: SVGSVGElement,
  ns: string,
  d: string,
  fill: string,
): void {
  const path = document.createElementNS(ns, "path");
  path.setAttribute("d", d);
  path.setAttribute("fill", fill);
  svg.appendChild(path);
}

function appendPaperOutline(group: SVGElement, ns: string): void {
  const outline = document.createElementNS(ns, "path");
  outline.setAttribute(
    "d",
    "M14 3.25H9.25A2.25 2.25 0 007 5.5v13A2.25 2.25 0 009.25 20.75h5.75A2.25 2.25 0 0017 18.5V9.125L14 3.25z",
  );
  outline.setAttribute("fill", "none");
  outline.setAttribute("stroke", "currentColor");
  outline.setAttribute("stroke-width", "1.35");
  outline.setAttribute("stroke-linejoin", "round");

  const fold = document.createElementNS(ns, "path");
  fold.setAttribute("d", "M14 3.25v4.75h4.45");
  fold.setAttribute("fill", "none");
  fold.setAttribute("stroke", "currentColor");
  fold.setAttribute("stroke-width", "1.35");
  fold.setAttribute("stroke-linecap", "round");
  fold.setAttribute("stroke-linejoin", "round");

  group.appendChild(outline);
  group.appendChild(fold);
}

/** Ruled plain-text sheet (no ubiquitous third-party trademark). */
function appendTextGlyphs(parent: SVGElement, ns: string): void {
  const ys = [11.85, 14.05, 16.2, 18.35];
  for (const y of ys) {
    const line = document.createElementNS(ns, "path");
    line.setAttribute("d", `M8.4 ${String(y)}h8.95`);
    line.setAttribute("fill", "none");
    line.setAttribute("stroke", "currentColor");
    line.setAttribute("stroke-width", "1.05");
    line.setAttribute("stroke-linecap", "round");
    line.setAttribute("opacity", "0.55");
    parent.appendChild(line);
  }
}

/**
 * Decorative document-type badge aligned with **`trainingMaterialIconKind`**.
 *
 * @param kind - Which glyph variant renders.
 * @returns **`aria-hidden`** **SVG**; attach next to readable filename text for context.
 */
export function createTrainingMaterialFileIcon(
  kind: TrainingMaterialIconKind,
): SVGSVGElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("width", "22");
  svg.setAttribute("height", "22");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");

  svg.classList.add(
    "participant-quiz__file-icon",
    `participant-quiz__file-icon--${kind}`,
  );

  switch (kind) {
    case "word":
      appendSimpleIconGlyph(svg, ns, SIMPLE_ICONS_MICROSOFT_WORD_D, "#2B579A");
      return svg;
    case "excel":
      appendSimpleIconGlyph(svg, ns, SIMPLE_ICONS_MICROSOFT_EXCEL_D, "#217346");
      return svg;
    case "pdf":
      appendSimpleIconGlyph(
        svg,
        ns,
        SIMPLE_ICONS_ADOBE_ACROBAT_READER_D,
        "#EC1C24",
      );
      return svg;
    default:
      break;
  }

  const g = document.createElementNS(ns, "g");

  appendPaperOutline(g, ns);

  if (kind === "text") {
    appendTextGlyphs(g, ns);
  }

  svg.appendChild(g);

  return svg;
}
